import React, { useState } from "react";
import { router } from "@inertiajs/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DashboardLayout from "../../components/DashboardLayout";
import { Save, User, Image as ImageIcon } from "lucide-react";

interface Owner {
  id?: number;
  name?: string;
  message?: string;
  photo_url?: string;
}

interface Client {
  id?: number;
  name: string;
  image_url?: string;
}

interface AboutData {
  id: number;
  description: string;
  mission: string;
  vision: string;
  owner?: Owner;
  clients: Client[];
}

interface AboutProps {
  about: AboutData;
  store_logo?: string;
  store_name?: string;
  auth: {
    admin: {
      email: string;
    };
  };
  errors?: Record<string, string>;
}

const About = ({ about, store_logo, store_name, errors }: AboutProps) => {
  const [formData, setFormData] = useState({
    description: about.description || "",
    mission: about.mission || "",
    vision: about.vision || "",
    owner_attributes: {
      id: about.owner?.id,
      name: about.owner?.name || "",
      message: about.owner?.message || "",
      photo: null as File | null,
    },
    clients_attributes: about.clients.map(client => ({
      id: client.id,
      name: client.name,
      image: null as File | null,
      image_url: client.image_url,
      _destroy: false,
    })),
  });

  const [newClient, setNewClient] = useState({ name: "", image: null as File | null });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOwnerChange = (field: string, value: string | File | null) => {
    setFormData((prev) => ({
      ...prev,
      owner_attributes: {
        ...prev.owner_attributes,
        [field]: value,
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data = new FormData();
    data.append("about[description]", formData.description);
    data.append("about[mission]", formData.mission);
    data.append("about[vision]", formData.vision);
    if (formData.owner_attributes.id) {
      data.append("about[owner_attributes][id]", formData.owner_attributes.id.toString());
    }
    data.append("about[owner_attributes][name]", formData.owner_attributes.name);
    data.append("about[owner_attributes][message]", formData.owner_attributes.message);
    if (formData.owner_attributes.photo) {
      data.append("about[owner_attributes][photo]", formData.owner_attributes.photo);
    }

    formData.clients_attributes.forEach((client, index) => {
      if (client.id) {
        data.append(`about[clients_attributes][${index}][id]`, client.id.toString());
      }
      data.append(`about[clients_attributes][${index}][name]`, client.name);
      if (client.image) {
        data.append(`about[clients_attributes][${index}][image]`, client.image);
      }
      if (client._destroy) {
        data.append(`about[clients_attributes][${index}][_destroy]`, "1");
      }
    });

    router.patch(`/admin/about`, data, {
      preserveScroll: true,
      onSuccess: () => {
        console.log("About updated successfully!");
      },
      onError: (errors) => {
        console.error("Failed to update about:", errors);
      },
    });
  };

  return (
    <DashboardLayout title="Gestión de Acerca de" storeLogo={store_logo} storeName={store_name}>
      <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/20 via-slate-400/20 to-slate-500/20 animate-pulse" />
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Gestión de Acerca de
            </h1>
            <p className="text-slate-50 text-lg">
              Configura la información de la página "Acerca de"
            </p>
          </div>
          <Button onClick={handleSubmit} className="bg-white text-slate-700 hover:bg-slate-50 shadow-lg hover:shadow-xl transition-all duration-200">
            <Save className="h-5 w-5 mr-2" />
            Guardar Cambios
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
      <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-r from-slate-500 to-slate-600 shadow-lg">
              <User className="h-6 w-6 text-white" />
            </div>
            Información General
          </CardTitle>
          <CardDescription className="text-base text-slate-600 dark:text-slate-400">
            Descripción, misión y visión de la empresa
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="description" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Descripción de la empresa"
              rows={4}
              className="border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg"
            />
            {errors?.description && <div className="text-red-500 text-sm">{errors.description}</div>}
          </div>
          <div className="space-y-3">
            <Label htmlFor="mission" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Misión</Label>
            <Textarea
              id="mission"
              value={formData.mission}
              onChange={(e) => handleInputChange("mission", e.target.value)}
              placeholder="Misión de la empresa"
              rows={3}
              className="border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg"
            />
            {errors?.mission && <div className="text-red-500 text-sm">{errors.mission}</div>}
          </div>
          <div className="space-y-3">
            <Label htmlFor="vision" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Visión</Label>
            <Textarea
              id="vision"
              value={formData.vision}
              onChange={(e) => handleInputChange("vision", e.target.value)}
              placeholder="Visión de la empresa"
              rows={3}
              className="border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg"
            />
            {errors?.vision && <div className="text-red-500 text-sm">{errors.vision}</div>}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-r from-slate-500 to-slate-600 shadow-lg">
              <User className="h-6 w-6 text-white" />
            </div>
            <span>Propietario</span>
          </CardTitle>
          <CardDescription className="text-base text-slate-600 dark:text-slate-400">
            Información del propietario o fundador
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="owner_name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nombre</Label>
            <Input
              id="owner_name"
              value={formData.owner_attributes.name}
              onChange={(e) => handleOwnerChange("name", e.target.value)}
              placeholder="Nombre del propietario"
              className="border-slate-300 dark:border-slate-600 focus:border-emerald-500 focus:ring-emerald-500/20 rounded-lg"
            />
            {errors?.["owner.name"] && <div className="text-red-500 text-sm">{errors["owner.name"]}</div>}
          </div>
          <div className="space-y-3">
            <Label htmlFor="owner_message" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Mensaje</Label>
            <Textarea
              id="owner_message"
              value={formData.owner_attributes.message}
              onChange={(e) => handleOwnerChange("message", e.target.value)}
              placeholder="Mensaje del propietario"
              rows={3}
              className="border-slate-300 dark:border-slate-600 focus:border-emerald-500 focus:ring-emerald-500/20 rounded-lg"
            />
            {errors?.["owner.message"] && <div className="text-red-500 text-sm">{errors["owner.message"]}</div>}
          </div>
          <div className="space-y-3">
            <Label htmlFor="owner_photo" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Foto</Label>
            <div className="flex items-center space-x-4">
              {about.owner?.photo_url ? (
                <img
                  src={about.owner.photo_url}
                  alt="Foto del propietario"
                  className="h-24 w-24 object-cover rounded-xl shadow-sm"
                />
              ) : (
                <div className="h-24 w-24 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl flex items-center justify-center bg-slate-50 dark:bg-slate-800">
                  <ImageIcon className="h-10 w-10 text-slate-400 dark:text-slate-500" />
                </div>
              )}
              <Input
                id="owner_photo"
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleOwnerChange("photo", e.target.files ? e.target.files[0] : null)
                }
                className="flex-1 border-slate-300 dark:border-slate-600 focus:border-emerald-500 focus:ring-emerald-500/20 rounded-lg"
              />
            </div>
            {errors?.["owner.photo"] && <div className="text-red-500 text-sm">{errors["owner.photo"]}</div>}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-r from-slate-500 to-slate-600 shadow-lg">
              <User className="h-6 w-6 text-white" />
            </div>
            <span>Clientes</span>
          </CardTitle>
          <CardDescription className="text-base text-slate-600 dark:text-slate-400">
            Gestiona los clientes asociados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {formData.clients_attributes.map((client, index) => (
            <div key={client.id || index} className="flex items-center space-x-4 p-6 border border-slate-200 dark:border-slate-700 rounded-xl bg-gradient-to-r from-white to-slate-50/50 dark:from-slate-800 dark:to-slate-700/50 hover:shadow-md transition-all duration-200">
              {client.image_url ? (
                <img
                  src={client.image_url}
                  alt={client.name}
                  className="h-16 w-16 object-cover rounded-lg shadow-sm"
                />
              ) : (
                <div className="h-16 w-16 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg flex items-center justify-center bg-slate-50 dark:bg-slate-800">
                  <ImageIcon className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                </div>
              )}
              <div className="flex-1">
                <Input
                  value={client.name}
                  onChange={(e) => {
                    const updated = [...formData.clients_attributes];
                    updated[index].name = e.target.value;
                    setFormData({ ...formData, clients_attributes: updated });
                  }}
                  placeholder="Nombre del cliente"
                  className="border-slate-300 dark:border-slate-600 focus:border-orange-500 focus:ring-orange-500/20 rounded-lg"
                />
              </div>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const updated = [...formData.clients_attributes];
                  updated[index].image = e.target.files ? e.target.files[0] : null;
                  setFormData({ ...formData, clients_attributes: updated });
                }}
                className="flex-1 border-slate-300 dark:border-slate-600 focus:border-orange-500 focus:ring-orange-500/20 rounded-lg"
              />
              <Button
                variant="destructive"
                onClick={() => {
                  const updated = [...formData.clients_attributes];
                  if (client.id) {
                    updated[index]._destroy = true;
                  } else {
                    updated.splice(index, 1);
                  }
                  setFormData({ ...formData, clients_attributes: updated });
                }}
                className="hover:bg-red-600 transition-colors duration-200"
              >
                Eliminar
              </Button>
            </div>
          ))}
          <div className="flex items-center space-x-4 p-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50/50 dark:bg-slate-800/50">
            <Input
              value={newClient.name}
              onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
              placeholder="Nombre del nuevo cliente"
              className="flex-1 border-slate-300 dark:border-slate-600 focus:border-orange-500 focus:ring-orange-500/20 rounded-lg"
            />
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setNewClient({ ...newClient, image: e.target.files ? e.target.files[0] : null })}
              className="flex-1 border-slate-300 dark:border-slate-600 focus:border-orange-500 focus:ring-orange-500/20 rounded-lg"
            />
            <Button
              onClick={() => {
                if (newClient.name.trim()) {
                  setFormData({
                    ...formData,
                    clients_attributes: [
                      ...formData.clients_attributes,
                      { id: undefined, name: newClient.name, image: newClient.image, image_url: undefined, _destroy: false }
                    ]
                  });
                  setNewClient({ name: "", image: null });
                }
              }}
              className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Agregar Cliente
            </Button>
          </div>
        </CardContent>
      </Card>
      </form>
    </DashboardLayout>
  );
};

export default About;